import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AuthenticationService } from '../authentication.service';
import { MotivesService } from '../motives.service';
import { FirestoreTimestamp } from './firestore-authentication.service';
import { AngularFirestoreCollection, FirestoreService } from './firestore.service';

import { CreateMotiveError, DeleteMotiveError, UpdateMotiveError } from '../../errors';
import { Motive } from '../../models';

export interface MotiveDTO {
    createdAt: FirestoreTimestamp;
    description: string;
    id: string;
    name: string;
}

@Injectable()
export class FirestoreMotivesService implements MotivesService {
    private get motivesCollection(): AngularFirestoreCollection<MotiveDTO> {
        return this.firestoreService.tenantDependentCollection<MotiveDTO>(this.authenticationService.user.tenantId, 'motives');
    }

    constructor(
        private authenticationService: AuthenticationService,
        private firestoreService: FirestoreService
    ) {}

    async createMotive(motive: Motive): Promise<void> {
        const tenantId = this.authenticationService.user.tenantId;
        const createMotive = this.firestoreService.cloudFunction('Motives_CreateMotive');
        const name = motive?.name;
        const description = motive?.description;
        const result = await createMotive({ name, description, tenantId });

        if (result && result.data && result.data.code) {
            switch (result.data.code) {
                case 'permission-denied':
                    throw new CreateMotiveError(
                        'PERMISSION_DENIED',
                        'The authenticated user don\'t have permissions to perform this action.'
                    );
                case 'invalid-argument':
                    throw new CreateMotiveError('MISSING_ARGUMENT', 'The property \'name\', \'description\' or \'tenantId\' is missing.');
                case 'already-exists':
                    throw new CreateMotiveError('NAME_ALREADY_EXISTS', 'There are already another \'Motive\' with this name.');
                default:
                    throw new CreateMotiveError('UNKNOWN', 'An unknown error occurred.');
            }
        }
    }

    async deleteMotive(motiveId: string): Promise<void> {
        const tenantId = this.authenticationService.user.tenantId;
        const deleteMotive = this.firestoreService.cloudFunction('Motives_DeleteMotive');

        const result = await deleteMotive({ motiveId, tenantId });

        if (result && result.data) {
            switch (result.data.code) {
                case 'permission-denied':
                    throw new DeleteMotiveError(
                        'PERMISSION_DENIED',
                        'The authenticated user don\'t have permissions to perform this action.'
                    );
                default:
                    throw new DeleteMotiveError('UNKNOWN', 'An unknown error occurred.');
            }
        }
    }

    async motive(motiveId: string): Promise<Motive | undefined> {
        return new Promise<Motive | undefined>((resolve, _) => {
            const motivesObservable = this.motivesCollection.doc(motiveId).get();
            const subscription = motivesObservable
                .pipe(
                    map((motiveDoc: any) => {
                        if (!motiveDoc.exists) {
                            return undefined;
                        }

                        const motiveDTO = motiveDoc.data() as MotiveDTO;
                        motiveDTO.id = motiveDoc.id;

                        return this.motiveMapper(motiveDTO);
                    })
                )
                .subscribe((motive: Motive) => {
                    resolve(motive);
                    subscription.unsubscribe();
                });
        });
    }

    motives(): Observable<Motive[]> {
        const motivesObservable = this.motivesCollection.snapshotChanges().pipe(
            map((result) => {
                return result.map((motiveDoc) => {
                    const motive = motiveDoc.payload.doc.data();
                    motive.id = motiveDoc.payload.doc.id;
                    return motive;
                });
            })
        );

        return motivesObservable.pipe(
            map((motivesDTOs: MotiveDTO[]) => {
                return motivesDTOs.map((motiveDTO) => {
                    return this.motiveMapper(motiveDTO);
                });
            })
        );
    }

    async updateMotive(motiveId: string, newMotiveContent: Motive): Promise<void> {
        const tenantId = this.authenticationService.user.tenantId;
        const updateMotive = this.firestoreService.cloudFunction('Motives_UpdateMotive');
        const result = await updateMotive({ motiveId, tenantId, ...newMotiveContent });

        if (result && result.data) {
            switch (result.data.code) {
                case 'permission-denied':
                    throw new UpdateMotiveError(
                        'PERMISSION_DENIED',
                        'The authenticated user don\'t have permissions to perform this action.'
                    );
                case 'invalid-argument':
                    throw new UpdateMotiveError(
                        'MISSING_ARGUMENT',
                        'The property \'motiveId\', \'name\', \'phone\' or \'tenantId\' is missing.'
                    );
                case 'already-exists':
                    throw new UpdateMotiveError('NAME_ALREADY_EXISTS', 'There are already another \'Motive\' with this name.');
                default:
                    throw new UpdateMotiveError('UNKNOWN', 'An unknown error occurred.');
            }
        }
    }

    private motiveMapper(motiveDTO: MotiveDTO): Motive {
        return {
            createdAt: motiveDTO.createdAt.toDate(),
            description: motiveDTO.description,
            id: motiveDTO.id,
            name: motiveDTO.name,
        };
    }
}
